<template>
  <div>
  
    <div class="card mb-4">
      <div class="card-header">
        <i class="fas fa-list"></i> {{ $t('fastQuantityEntry') }}
      </div>
      <div class="card-body">
        <p class="text-muted mb-3">{{ $t('fastQuantityEntryHelp') }}</p>
        <div class="mb-3">
          <label for="quickLabelsInput" class="form-label">{{ $t('quickQuantityLabels') }}</label>
          <input
            id="quickLabelsInput"
            type="text"
            class="form-control"
            :placeholder="$t('quickQuantityLabelsPlaceholder')"
            v-model="quantityQuickLabelsInput"
          >
          <small class="form-text text-muted">{{ $t('quickQuantityLabelsHelp') }}</small>
        </div>
      </div>
    </div>

    <div class="card mb-4" v-if="config?.rails === 'olo'">
      <div class="card-header">
        <i class="fas fa-ticket-alt"></i> {{ $t('oloVoucherPrintSectionTitle') }}
      </div>
      <div class="card-body">
        <p class="text-muted mb-3">{{ $t('oloVoucherPrintLabelsHelp') }}</p>
        <div class="mb-3">
          <label for="oloVoucherLabelsInput" class="form-label">{{ $t('oloVoucherPrintLabels') }}</label>
          <input
            id="oloVoucherLabelsInput"
            type="text"
            class="form-control"
            :placeholder="$t('oloVoucherPrintLabelsPlaceholder')"
            v-model="voucherPrintLabelsInput"
          >
        </div>
        <h6 class="mt-4">{{ $t('oloVoucherMatchingItems') }}</h6>
        <p v-if="matchingVoucherMenuItems.length === 0" class="text-muted small mb-0">{{ $t('oloVoucherNoMatchingItems') }}</p>
        <ul v-else class="list-group list-group-flush mb-3 voucher-match-list">
          <li
            v-for="item in matchingVoucherMenuItems"
            :key="item.ref"
            class="list-group-item px-0 d-flex justify-content-between align-items-start"
          >
            <div>
              <span class="fw-bold">{{ item.name }}</span>
              <small v-if="voucherMatchHint(item)" class="text-muted d-block">{{ voucherMatchHint(item) }}</small>
            </div>
            <span v-if="typeof item.price === 'number'" class="text-muted text-nowrap ms-2">${{ formatPrice(item.price) }}</span>
          </li>
        </ul>
        <h6>{{ $t('oloVoucherTicketPreview') }}</h6>
        <p class="text-muted small mb-2">{{ $t('oloVoucherPreviewHint') }}</p>
        <div class="voucher-ticket-preview" aria-hidden="true">
          <div class="voucher-ticket-preview-inner">
          <div
            v-for="(line, idx) in voucherPreviewLines"
            :key="'vprev-' + idx"
          >
            <div
              v-if="line.type === 'Text'"
              class="preview-line text-center"
              :class="{
                'preview-separator': isVoucherPreviewSeparatorLine(line.text),
                'preview-bold': line.bold,
                'preview-double': line.size === 'DOUBLE',
                'preview-large': line.size === 'LARGE',
                'preview-normal': line.size === 'NORMAL',
                'preview-small': line.size === 'SMALL'
              }"
            >{{ line.text }}</div>
            <div
              v-else-if="line.type === 'Empty'"
              class="preview-empty-spacer"
              :style="{ height: ((line.count || 1) * 0.4) + 'rem' }"
            />
          </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('loading') }}</span>
      </div>
      <p class="mt-3">{{ $t('loadingMenuData') }}</p>
    </div>
    
    <div v-if="!isLoading">
      <div class="card mb-4">
        <div class="card-header">
          <i class="fas fa-utensils"></i> {{ $t('fastItems') }}
        </div>
        <div class="card-body">
          <div v-if="displayFastItems.length > 0" class="mb-4">
            <div class="list-group">
              <div v-for="(element, index) in displayFastItems" :key="element.fastId" class="list-group-item">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <span class="fw-bold">
                      {{ element.customName || element.name || getItemNameById(element.itemRef) || $t('unknownItem') }}
                      <small v-if="element.customName" class="text-muted fst-italic ms-1">({{ element.name || getItemNameById(element.itemRef) || $t('baseItem') }})</small>
                    </span>
                    <span class="badge ms-2" :class="element.canBeModified ? 'bg-success' : 'bg-warning'">
                      {{ element.canBeModified ? $t('modifiable') : $t('locked') }}
                    </span>
                    <span class="badge bg-info text-dark ms-2">
                      {{ $t('group') }}: {{ element.groupName || $t('featured') }}
                    </span>
                    <small class="text-muted ms-2">
                      {{ getTotalModifiers(element) }} {{ $t(getTotalModifiers(element) !== 1 ? 'modifiers' : 'modifier') }}
                    </small>
                  </div>
                  <div class="d-flex align-items-center">
                    <button class="btn btn-sm btn-outline-secondary me-2" @click="moveItem(index, -1)" :disabled="index === 0" aria-label="Move up">
                      <i class="fas fa-arrow-up"></i>
                    </button>
                    <button class="btn btn-sm btn-outline-secondary me-2" @click="moveItem(index, 1)" :disabled="index === displayFastItems.length - 1" aria-label="Move down">
                      <i class="fas fa-arrow-down"></i>
                    </button>
                    <button class="btn btn-sm btn-outline-secondary me-2" @click="openEditItemModal(element)">{{ $t('edit') }}</button>
                    <button class="btn btn-sm btn-outline-danger" @click="deleteFastItem(element.fastId)">{{ $t('delete') }}</button>
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-3">
              <button class="btn btn-sm btn-outline-secondary" @click="sortItemsByName">{{ $t('sortByName') }}</button>
              <button class="btn btn-sm btn-outline-secondary ms-2" @click="sortItemsByGroup">{{ $t('sortByGroup') }}</button>
              <button class="btn btn-sm btn-outline-secondary ms-2" @click="saveCurrentOrder">{{ $t('saveCurrentOrder') }}</button>
            </div>
          </div>
          <div v-else class="alert alert-info">
            {{ $t('noFastItems') }}
          </div>
          
          <div v-if="availableMenuList.length === 0" class="alert alert-warning mb-3">
            <i class="fas fa-exclamation-triangle me-2"></i>
            {{ $t('noMenuItems') }}
            <button class="btn btn-outline-primary btn-sm ms-3" @click="forceReloadMenuData">
              <i class="fas fa-sync-alt me-1"></i> {{ $t('reloadMenuData') }}
            </button>
          </div>
          
          <button class="btn btn-primary" style="float: right" @click="openAddItemModal">
            {{ $t('addFastItem') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Item Modal -->
    <div v-if="showAddItemModal" class="modal-backdrop fade show"></div>
    <div class="modal fade" :class="{ 'show': showAddItemModal }" tabindex="-1" style="display: block;" v-if="showAddItemModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEditing ? $t('editFastItem') : $t('addFastItem') }}</h5>
            <button type="button" class="btn-close" @click="closeAddItemModal"></button>
          </div>
          
          <div class="modal-body">
            <!-- Step 1: Search and Select Item (Only for Add) -->
            <div v-if="!isEditing && currentStep === 0">
              <h5>{{ $t('selectMenuItem') }}</h5>
              <div class="input-group mb-3">
                <input type="text" class="form-control" :placeholder="$t('searchForItem')" 
                       v-model="searchTerm" autofocus>
                <button class="btn btn-outline-secondary" type="button" @click="searchTerm = ''" 
                        v-if="searchTerm">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              
              <div class="list-group item-list mb-3" style="max-height: 300px; overflow-y: auto;">
                <a v-for="item in filteredAvailableItems" :key="item.ref"
                   class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                   :class="{'active': selectedItemConfig.itemRef === item.ref}"
                   @click="selectItemForConfiguration(item)">
                  <span>{{ item.name }}</span>
                  <span class="badge bg-light text-dark">${{ formatPrice(item.price) }}</span>
                </a>
              </div>
              
              <div v-if="selectedItemConfig.itemRef" class="alert alert-primary d-flex align-items-center mb-0">
                <span class="me-auto">{{ $t('selected') }}: <strong>{{ selectedItemDetails?.name }}</strong></span>
                <button class="btn btn-primary btn-sm" @click="currentStep++">
                  {{ $t('continue') }}
                </button>
              </div>
            </div>
            
            <!-- Step 2: Configure Modifiers -->
            <div v-if="(isEditing && currentStep === 0) || (!isEditing && currentStep === 1)">
              <h5>{{ $t('configureModifiers') }}</h5>
              
              <div v-if="selectedItemDetails && Array.isArray(selectedItemDetails.modifierSets) && selectedItemDetails.modifierSets.length > 0">
                <!-- Required Modifier Sets First -->
                <div v-if="requiredModSets.length > 0" class="mb-4">
                  <h6><span class="badge bg-danger me-2">{{ $t('required') }}</span> {{ $t('modifierSets') }}</h6>
                  <div class="mod-sets-container">
                    <div v-for="modSet in requiredModSets" :key="modSet.ref" 
                         class="card mb-3">
                      <div class="card-header">
                        <h6 class="mb-0">{{ modSet.name }}</h6>
                      </div>
                      <div class="card-body">
                        <div v-for="modifier in modSet.modifiers" :key="modifier.ref"
                             class="form-check">
                          <input
                            class="form-check-input"
                            :type="modSet.max === 1 ? 'radio' : 'checkbox'"
                            :name="modSet.ref"
                            :id="modifier.ref"
                            :value="modifier.ref"
                            :checked="isModifierSelected(modSet.ref, modifier.ref)"
                            @change="toggleModifierSelection(modSet.ref, modifier.ref, modSet.max === 1)">
                          <label class="form-check-label d-flex justify-content-between w-100" :for="modifier.ref">
                            <span>{{ modifier.name }}</span>
                            <span v-if="modifier.price > 0" class="text-muted">
                              +${{ formatPrice(modifier.price) }}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Optional Modifier Sets Second -->
                <div v-if="optionalModSets.length > 0" class="mb-4">
                  <h6><span class="badge bg-secondary me-2">{{ $t('optional') }}</span> {{ $t('modifierSets') }}</h6>
                  <div class="mod-sets-container">
                    <div v-for="modSet in optionalModSets" :key="modSet.ref" 
                         class="card mb-3">
                      <div class="card-header">
                        <h6 class="mb-0">{{ modSet.name }}</h6>
                      </div>
                      <div class="card-body">
                        <div v-for="modifier in modSet.modifiers" :key="modifier.ref"
                             class="form-check">
                          <input
                            class="form-check-input"
                            :type="modSet.max === 1 ? 'radio' : 'checkbox'"
                            :name="modSet.ref"
                            :id="modifier.ref"
                            :value="modifier.ref"
                            :checked="isModifierSelected(modSet.ref, modifier.ref)"
                            @change="toggleModifierSelection(modSet.ref, modifier.ref, modSet.max === 1)">
                          <label class="form-check-label d-flex justify-content-between w-100" :for="modifier.ref">
                            <span>{{ modifier.name }}</span>
                            <span v-if="modifier.price > 0" class="text-muted">
                              +${{ formatPrice(modifier.price) }}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="text-end">
                  <button v-if="!isEditing" class="btn btn-outline-secondary me-2" @click="currentStep--">
                    {{ $t('back') }}
                  </button>
                  <button class="btn btn-primary" @click="currentStep++">
                    {{ $t('continue') }}
                  </button>
                </div>
              </div>
              <div v-else class="alert alert-info">
                {{ $t('noModifiersToConfigure') }}
                <div class="text-end mt-2">
                  <button class="btn btn-primary" @click="currentStep++">
                    {{ $t('continue') }}
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Step 3: Settings -->
            <div v-if="(isEditing && currentStep === 1) || (!isEditing && currentStep === 2)">
              <h5>{{ $t('settings') }}</h5>
              
              <div class="card mb-3">
                <div class="card-body">
                  <div class="mb-3">
                      <label for="customNameInput" class="form-label">{{ $t('customNameOptional') }}</label>
                      <input type="text" class="form-control" id="customNameInput" 
                             :placeholder="$t('leaveBlankToUseOriginalItemName')"
                             v-model="selectedItemConfig.customName">
                  </div>
                  <div class="mb-3">
                      <label for="customDescriptionInput" class="form-label">{{ $t('customDescriptionOptional') }}</label>
                      <textarea class="form-control" id="customDescriptionInput" rows="3"
                                :placeholder="$t('leaveBlankToUseOriginalItemDescription')"
                                v-model="selectedItemConfig.customDescription"></textarea>
                  </div>
                  <div class="mb-3">
                      <label for="groupNameInput" class="form-label">{{ $t('groupName') }}</label>
                      <div class="input-group">
                        <select class="form-select" v-model="selectedItemConfig.groupName" aria-label="Select group" 
                                @change="handleGroupSelection">
                          <option value="">{{ $t('createNewGroup') }}</option>
                          <option v-for="group in existingGroups" :key="group" :value="group">{{ group }}</option>
                        </select>
                        <input type="text" class="form-control" v-if="!selectedItemConfig.groupName" 
                               :placeholder="$t('enterNewGroupName')" v-model="newGroupName" ref="newGroupNameInput"
                               @blur="applyNewGroupName">
                      </div>
                      <small class="form-text text-muted">{{ $t('groupNameHelp', { default: lastUsedGroup || $t('featured') }) }}</small>
                  </div>
                  <hr>
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" role="switch" 
                           id="canBeModifiedToggle" v-model="selectedItemConfig.canBeModified">
                    <label class="form-check-label" for="canBeModifiedToggle">
                      {{ $t('allowItemModified') }}
                    </label>
                  </div>
                </div>
              </div>
              
              <div class="alert" :class="selectedItemConfig.canBeModified ? 'alert-info' : 'alert-warning'">
                {{ selectedItemConfig.canBeModified ? 
                  $t('allowItemModifiedHelp') : 
                  $t('disallowItemModifiedHelp')
                }}
              </div>
              
              <div class="text-end mt-3">
                <button class="btn btn-outline-secondary me-2" @click="currentStep--">
                  {{ $t('back') }}
                </button>
                <button type="button" class="btn btn-success" @click="saveFastItem">
                  {{ $t('save') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Save Button -->
  <save-button :isDirty="isSettingsDirty" @save="saveSettings" :stickyBottom="true" :serverDone="serverDone"></save-button>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import SaveButton from '@/components/features/admin/SaveButton.vue';
import {
  buildOloVoucherChitLines,
  menuItemMatchesVoucherTriggers,
} from '@/utils/oloVoucher';

export default {
  name: 'AdminMenuComponent',
  data() {
    return {
      showAddItemModal: false,
      isEditing: false,
      searchTerm: '',
      currentStep: 0, // Track current step in the modal process
      // Steps for the modal progress indicator
      addSteps: ['Select Item', 'Configure Modifiers', 'Settings'],
      editSteps: ['Configure Modifiers', 'Settings'],
      // Holds the configuration for the item being added/edited
      selectedItemConfig: {
        fastId: null, // Unique ID for THIS fast item instance
        itemRef: null,
        defaultMods: [], // Structure: [{ modSetRef: '...', mods: ['...'] }]
        canBeModified: true,
        order: 0,
        name: '', // Store base name for reference
        customName: '',
        customDescription: '',
        groupName: '', // Add groupName field
      },
      localFastItems: [], // Local copy for manual sorting
      isLoading: true,
      newGroupName: '', // For creating new group names
      lastUsedGroup: '', // Track the last used group
      serverDone: false,
      quantityQuickLabelsInput: '',
      voucherPrintLabelsInput: '',
    };
  },
  components: {
    SaveButton
  },
  computed: {
    ...mapState('admin', [
      'config',
      'menuItems' // Get menu items directly from admin store
    ]),
    availableMenuList() {
      // Prefer items from admin store; fallback to config
      if (this.menuItems && this.menuItems.length > 0) return this.menuItems;
      return this.config?.menu || this.config?.menuData || [];
    },
    fastItemsConfig() {
      return this.config?.fastItems || [];
    },
    // Creates a sorted list with item details for display
    displayFastItems() {
        // Ensure localFastItems is populated and sorted first
        const sortedLocal = [...this.localFastItems].sort((a, b) => a.order - b.order);
        
        return sortedLocal.map(fastItem => {
          // Find the menu item to get the base name if needed
          const menuItem = this.availableMenuList.find(item => item.ref === fastItem.itemRef);
          const baseName = menuItem?.name || fastItem.name || this.$t('unknownItem'); // Use stored name as fallback

          return {
            ...fastItem,
            // Use custom name if available, otherwise fallback
            name: fastItem.customName || baseName, 
            // Store the original base name separately if needed elsewhere, but primary name is overridden
            baseName: baseName 
          };
        });
    },
    // Menu items available to be added as fast items (not already added)
    availableMenuItems() {
      // console.log('Computing availableMenuItems',
      //            'menuItems:', this.availableMenuList.length);
      // No longer filter out items already added as fast items
      return this.availableMenuList;
    },
    // Filter available items based on search term
    filteredAvailableItems() {
      if (!this.searchTerm) {
        return this.availableMenuItems;
      }
      const lowerSearchTerm = this.searchTerm.toLowerCase();
      return this.availableMenuItems.filter(item => 
        item.name.toLowerCase().includes(lowerSearchTerm)
      );
    },
    // Get full details of the item selected for configuration
    selectedItemDetails() {
        if (!this.selectedItemConfig.itemRef) return null;
        return this.availableMenuList.find(item => item.ref === this.selectedItemConfig.itemRef);
    },
    // Separate modifier sets into required and optional
    requiredModSets() {
      if (!this.selectedItemDetails) return [];
      return this.selectedItemDetails.modifierSets.filter(modSet => modSet.min > 0);
    },
    optionalModSets() {
      if (!this.selectedItemDetails) return [];
      return this.selectedItemDetails.modifierSets.filter(modSet => modSet.min === 0);
    },
    existingGroups() {
      // Get all unique group names from existing fast items
      const groups = new Set();
      this.localFastItems.forEach(item => {
        if (item.groupName && item.groupName.trim()) {
          groups.add(item.groupName.trim());
        }
      });
      // Always add "Featured" as default
      groups.add(this.$t('featured'));
      // Sort alphabetically
      return [...groups].sort();
    },
    voucherTriggerLabelsParsed() {
      return (this.voucherPrintLabelsInput || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => s.toUpperCase());
    },
    matchingVoucherMenuItems() {
      const triggers = this.voucherTriggerLabelsParsed;
      if (!triggers.length) return [];
      return this.availableMenuList.filter((item) =>
        menuItemMatchesVoucherTriggers(item, triggers)
      );
    },
    voucherPreviewLines() {
      const first = this.matchingVoucherMenuItems[0];
      const name = first?.name || 'Sample item name';
      const cents = typeof first?.price === 'number' ? first.price : 999;
      return buildOloVoucherChitLines({
        itemName: name,
        valueCents: cents,
      });
    },
    // Check if settings have been modified
    isSettingsDirty() {
      const parseSorted = (input) =>
        (input || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .map((s) => s.toLowerCase())
          .sort();
      const arraysEqual = (a, b) => {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
          if (a[i] !== b[i]) return false;
        }
        return true;
      };

      const currentQ = parseSorted(this.quantityQuickLabelsInput);
      const savedQ = Array.isArray(this.config?.quantityQuickLabels)
        ? this.config.quantityQuickLabels
            .map((s) => String(s || '').trim().toLowerCase())
            .filter(Boolean)
            .sort()
        : [];
      if (!arraysEqual(currentQ, savedQ)) return true;

      if (this.config?.rails === 'olo') {
        const currentV = parseSorted(this.voucherPrintLabelsInput);
        const rawSavedV = this.config?.oloVoucherPrintLabels;
        const savedV = Array.isArray(rawSavedV)
          ? rawSavedV.map((s) => String(s || '').trim().toLowerCase()).filter(Boolean).sort()
          : parseSorted('VOUCHER');
        if (!arraysEqual(currentV, savedV)) return true;
      }

      return false;
    },
  },
  methods: {
    ...mapActions('admin', [
      'fetchConfig', 
      'saveConfig',
      'loadMenuData' // Use loadMenuData from admin store
    ]),
    async saveSettings() {
      try {
        this.serverDone = false;
        const labels = (this.quantityQuickLabelsInput || '')
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);
        const voucherLabels = (this.voucherPrintLabelsInput || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        const updated = {
          ...this.config,
          quantityQuickLabels: labels,
          ...(this.config?.rails === 'olo' ? { oloVoucherPrintLabels: voucherLabels } : {}),
        };
        await this.saveConfig(updated);
        this.serverDone = true;
      } catch (e) {
        console.error('Failed to save quick quantity labels', e);
        alert(this.$t('saveError'));
        this.serverDone = true;
      }
    },

    // Format price from cents to dollars
    formatPrice(cents) {
      const dollars = (cents / 100).toFixed(2);
      return dollars;
    },

    voucherMatchHint(item) {
      const triggers = this.voucherTriggerLabelsParsed;
      if (!triggers.length) return '';
      const hits = [...(item.tags || []), ...(item.customLabels || [])]
        .map((x) => String(x || '').trim())
        .filter((x) => triggers.includes(x.toUpperCase()));
      return [...new Set(hits)].join(', ');
    },

    /** Receipt rule lines are '=' runs; keep on one line in preview (narrow box was wrapping them). */
    isVoucherPreviewSeparatorLine(text) {
      return typeof text === 'string' && text.length > 0 && /^=+$/.test(text);
    },

    // --- Modal Control ---
    openAddItemModal() {
      this.resetModalState();
      this.isEditing = false;
      this.currentStep = 0;
      this.showAddItemModal = true;
    },
    openEditItemModal(fastItemToEdit) {
       this.resetModalState();
       this.isEditing = true;
       this.currentStep = 0; // Edit mode starts at modifier configuration
       
       // Deep clone the passed item directly. It should contain its unique fastId.
       this.selectedItemConfig = JSON.parse(JSON.stringify(fastItemToEdit));
       
       // Ensure itemRef is properly set for selectedItemDetails to work
       // (it should already be there from fastItemToEdit)
       if (!this.selectedItemConfig.itemRef) {
           console.error("Fast item to edit is missing itemRef:", fastItemToEdit);
           // Potentially fetch item details if only fastId was available and itemRef was missing,
           // but typically fastItemToEdit should be a complete object from localFastItems.
       }

       this.showAddItemModal = true;
    },
    closeAddItemModal() {
      this.showAddItemModal = false;
      this.resetModalState();
    },
    resetModalState() {
       this.searchTerm = '';
       this.isEditing = false;
       this.currentStep = 0;
       this.selectedItemConfig = {
           fastId: null, // Reset fastId
           itemRef: null,
           defaultMods: [],
           canBeModified: true,
           order: 0,
           name: '', // Reset base name
           customName: '', // Reset custom name
           customDescription: '', // Reset custom description
           groupName: this.lastUsedGroup || this.$t('featured'), // Use last used group or Featured
       };
       this.newGroupName = '';
    },

    // --- Item Selection ---
    selectItemForConfiguration(item) {
       this.selectedItemConfig.itemRef = item.ref;
       this.selectedItemConfig.name = item.name; // Store base name
       this.selectedItemConfig.defaultMods = []; // Reset mods when item changes
       this.searchTerm = ''; // Clear search
    },

    // --- Modifier Selection ---
    isModifierSelected(modSetRef, modifierRef) {
       const modSet = this.selectedItemConfig.defaultMods.find(ms => ms.modSetRef === modSetRef);
       return modSet ? modSet.mods.includes(modifierRef) : false;
    },
    toggleModifierSelection(modSetRef, modifierRef, isRadio) {
        let modSet = this.selectedItemConfig.defaultMods.find(ms => ms.modSetRef === modSetRef);

        // If the modifier set isn't in our defaultMods array yet, add it
        if (!modSet) {
            modSet = { modSetRef: modSetRef, mods: [] };
            this.selectedItemConfig.defaultMods.push(modSet);
        }

        const modIndex = modSet.mods.indexOf(modifierRef);
        const details = this.selectedItemDetails && Array.isArray(this.selectedItemDetails.modifierSets)
          ? this.selectedItemDetails.modifierSets.find(ms => ms.ref === modSetRef)
          : null;
        const maxAllowed = typeof (details && details.max) === 'number' ? details.max : Infinity;

        if (modIndex > -1) {
            // If it's selected, remove it (unless it's a radio button trying to deselect)
            if (!isRadio) {
               modSet.mods.splice(modIndex, 1);
            }
        } else {
            // If it's not selected, add it
            if (isRadio) {
                // For radio, clear existing selection in this set
                modSet.mods = [modifierRef];
            } else {
                // For checkbox, check max constraint
                if (modSet.mods.length < maxAllowed) {
                   modSet.mods.push(modifierRef);
                } else {
                   // Optional: provide feedback that max is reached
                   console.warn(`Max selection reached for ${modSetRef}`);
                   // Re-check the box visually if needed (since the change event fired but we didn't update)
                   this.$nextTick(() => {
                      const checkbox = document.getElementById(modifierRef);
                      if (checkbox) checkbox.checked = true;
                   });
                }
            }
        }

        // Clean up empty mod sets
        this.selectedItemConfig.defaultMods = this.selectedItemConfig.defaultMods.filter(ms => ms.mods.length > 0);
    },

    // --- Manual Sorting Methods ---
    moveItem(index, direction) {
      if ((direction < 0 && index === 0) || (direction > 0 && index === this.localFastItems.length - 1)) {
        return; // Can't move beyond boundaries
      }
      
      const newIndex = index + direction;
      const items = [...this.localFastItems];
      
      // Swap the items
      [items[index], items[newIndex]] = [items[newIndex], items[index]];
      
      // Update order properties
      items.forEach((item, idx) => {
        item.order = idx;
      });
      
      this.localFastItems = items;
    },
    
    sortItemsByName() {
      const sorted = [...this.localFastItems].sort((a, b) => {
        const nameA = a.customName || a.name || this.getItemNameById(a.itemRef) || '';
        const nameB = b.customName || b.name || this.getItemNameById(b.itemRef) || '';
        return nameA.localeCompare(nameB);
      });
      
      // Update order values
      sorted.forEach((item, index) => {
        item.order = index;
      });
      
      this.localFastItems = sorted;
    },
    
    sortItemsByGroup() {
      const sorted = [...this.localFastItems].sort((a, b) => {
        // First sort by group name
        const groupA = a.groupName || this.$t('featured');
        const groupB = b.groupName || this.$t('featured');
        const groupCompare = groupA.localeCompare(groupB);
        
        // If same group, sort by name
        if (groupCompare === 0) {
          const nameA = a.customName || a.name || this.getItemNameById(a.itemRef) || '';
          const nameB = b.customName || b.name || this.getItemNameById(b.itemRef) || '';
          return nameA.localeCompare(nameB);
        }
        
        return groupCompare;
      });
      
      // Update order values
      sorted.forEach((item, index) => {
        item.order = index;
      });
      
      this.localFastItems = sorted;
    },
    
    async saveCurrentOrder() {
      try {
        // Create updated config with new fastItems array and save it
        const updatedConfig = { ...this.config, fastItems: this.localFastItems };
        await this.saveConfig(updatedConfig);
        alert(this.$t('orderSavedSuccess'));
      } catch (error) {
        console.error('Error saving item order:', error);
        alert(this.$t('orderSavedError'));
      }
    },

    // --- Saving ---
    async saveFastItem() {
       // Validation (basic): Check if min requirements for default mods are met
       if (this.selectedItemDetails) {
           for (const modSetDef of this.selectedItemDetails.modifierSets) {
               if (modSetDef.min > 0) {
                   const selectedInSet = this.selectedItemConfig.defaultMods.find(ms => ms.modSetRef === modSetDef.ref);
                   const count = selectedInSet ? selectedInSet.mods.length : 0;
                   if (count < modSetDef.min) {
                       alert(this.$t('minSelectionRequired', { min: modSetDef.min, name: modSetDef.name }));
                       return; // Stop saving
                   }
               }
           }
       }

      // Store the name directly in the fast item config for display fallback
      if (this.selectedItemDetails && this.selectedItemDetails.name) {
        this.selectedItemConfig.name = this.selectedItemDetails.name;
      }
      
      // Ensure groupName is not empty
      const groupName = this.selectedItemConfig.groupName?.trim() || this.newGroupName?.trim() || this.lastUsedGroup || this.$t('featured');
      if (!groupName) {
        alert(this.$t('groupNameEmptyError'));
        return;
      }
      
      // Update last used group
      this.lastUsedGroup = groupName;
      
      // Clean up empty strings for custom fields before saving
      const configToSave = {
        ...this.selectedItemConfig,
        groupName: groupName,
        customName: this.selectedItemConfig.customName?.trim() || null,
        customDescription: this.selectedItemConfig.customDescription?.trim() || null,
      };

      let updatedFastItems;
      if (this.isEditing) {
        // Editing: configToSave.fastId should already exist from openEditItemModal
        if (!configToSave.fastId) {
            console.error("Editing fast item, but fastId is missing!", configToSave);
            alert(this.$t('fastItemIdMissingError'));
            return;
        }
        const index = this.localFastItems.findIndex(fi => fi.fastId === configToSave.fastId);
        if (index !== -1) {
            // Preserve the original order
            configToSave.order = this.localFastItems[index].order;
            updatedFastItems = [...this.localFastItems];
            updatedFastItems[index] = configToSave; // Save the cleaned config
        } else {
           console.error("Error finding item to update during edit using fastId:", configToSave.fastId);
           alert(this.$t('itemUpdateError'));
           return;
        }
      } else {
        // Adding a new item, assign the next order number and a unique fastId
        configToSave.fastId = Date.now().toString(36) + Math.random().toString(36).substring(2);
        const maxOrder = this.localFastItems.reduce((max, item) => Math.max(max, item.order), -1);
        configToSave.order = maxOrder + 1;
        updatedFastItems = [...this.localFastItems, configToSave]; // Save the cleaned config
      }

      try {
        // Create updated config with new fastItems array
        const updatedConfig = { ...this.config, fastItems: updatedFastItems };
        
        // Save the entire config
        await this.saveConfig(updatedConfig);
        
        // Update local copy after successful save
        this.localFastItems = updatedFastItems;
        
        this.closeAddItemModal();
      } catch (error) {
        console.error('Error saving fast item:', error);
        alert(this.$t('fastItemSavedError'));
      }
    },

    // --- Deleting ---
    async deleteFastItem(fastIdToDelete) { // Changed parameter name
       if (!confirm(this.$t('confirmDeleteFastItem'))) {
           return;
       }
       // Filter by fastId now
       const updatedFastItems = this.localFastItems.filter(fi => fi.fastId !== fastIdToDelete)
                                    .map((fi, index) => ({ ...fi, order: index })); // Re-order after deletion

       try {
         // Create updated config with new fastItems array
         const updatedConfig = { ...this.config, fastItems: updatedFastItems };
         
         // Save the entire config
         await this.saveConfig(updatedConfig);
         
         // Update local copy after successful save
         this.localFastItems = updatedFastItems;
       } catch (error) {
         console.error('Error deleting fast item:', error);
         alert(this.$t('fastItemDeleteError'));
       }
    },

    // --- Initialization ---
    syncLocalFastItems() {
       // Deep clone to prevent direct mutation of Vuex state
       const itemsFromConfig = JSON.parse(JSON.stringify(this.config?.fastItems || []));
       
       this.localFastItems = itemsFromConfig.map((item, index) => {
         if (!item.fastId) {
           // If an old item doesn't have fastId, generate one.
           // This ensures all items in localFastItems have a unique key for v-for and operations.
           item.fastId = Date.now().toString(36) + Math.random().toString(36).substring(2, 9) + '_' + index;
         }
         return item;
       });
    },

    // Helper method to count total modifiers selected for an item
    getTotalModifiers(fastItem) {
      if (!fastItem.defaultMods) return 0;
      
      return fastItem.defaultMods.reduce((total, modSet) => {
        return total + modSet.mods.length;
      }, 0);
    },

    // Add this to the methods section
    getItemNameById(itemRef) {
      if (!itemRef) return null;
      
      // Try to find in availableMenuList
      const menuItem = this.availableMenuList.find(item => item.ref === itemRef);
      if (menuItem && menuItem.name) {
        return menuItem.name;
      }
      
      // Try to find in config.menu
      if (this.config && this.config.menu) {
        const configMenuItem = this.config.menu.find(item => item.ref === itemRef);
        if (configMenuItem && configMenuItem.name) {
          return configMenuItem.name;
        }
      }
      
      return null;
    },

    applyNewGroupName() {
      if (this.newGroupName.trim()) {
        this.selectedItemConfig.groupName = this.newGroupName.trim();
        this.lastUsedGroup = this.newGroupName.trim(); // Remember last used group
        this.newGroupName = '';
      } else if (!this.selectedItemConfig.groupName) {
        // If no new name and no selection, set to last used or Featured
        this.selectedItemConfig.groupName = this.lastUsedGroup || 'Featured';
      }
    },

    handleGroupSelection() {
      // If user selects empty (create new) and then doesn't enter anything, revert to default
      if (!this.selectedItemConfig.groupName) {
        this.$nextTick(() => {
          if (this.$refs && this.$refs.newGroupNameInput) {
            this.$refs.newGroupNameInput.focus();
          }
        });
      }
    },

    // Update the forceReloadMenuData method to use the admin store's loadMenuData
    async forceReloadMenuData() {
      this.isLoading = true;
      try {
        // Use the admin store's loadMenuData
        await this.loadMenuData();
      } catch (error) {
        console.error('Failed to reload menu data:', error);
      } finally {
        this.isLoading = false;
      }
    },
  },
  watch: {
    // Keep local selection in sync with config (null-safe)
    config: {
      handler(val) {
        const labels = Array.isArray(val?.quantityQuickLabels) ? val.quantityQuickLabels : [];
        this.quantityQuickLabelsInput = labels.join(', ');
        const vLabels = val?.oloVoucherPrintLabels;
        if (Array.isArray(vLabels)) {
          this.voucherPrintLabelsInput = vLabels.join(', ');
        } else {
          this.voucherPrintLabelsInput = 'VOUCHER';
        }
      },
      deep: true,
      immediate: true
    },
    // Watch the source config from Vuex and update local copy if it changes elsewhere
    fastItemsConfig: {
        handler() {
            this.syncLocalFastItems();
        },
        deep: true,
        immediate: true // Sync immediately on component mount
    }
  },
  mounted() {
      this.syncLocalFastItems(); // Initial sync
      
      // Load both admin config and menu data
      this.isLoading = true;
      
      // Try to load menu data
      Promise.all([
        this.loadMenuData().catch(error => {
          console.error('Error loading menu data:', error);
          return null; // Continue with other promises even if this fails
        }),
        this.fetchConfig().catch(error => {
          console.error('Error loading config:', error);
          return null; // Continue with other promises even if this fails
        })
      ]).then(() => {
          
          // No department-based quantity settings to initialize
          
          this.isLoading = false;
      }).catch(error => {
          console.error('Error loading data:', error);
          this.isLoading = false;
      });
  }
};
</script>

<style scoped>
.admin-instructions {
  margin-bottom: 1.5rem;
}

.card {
  margin-bottom: 1.5rem;
}

.card-header {
  font-weight: 500;
  background-color: #f8f9fa;
}

.list-group-item {
  transition: all 0.2s;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.modal {
  background-color: rgba(0,0,0,0.5);
}

.item-list {
  max-height: 300px;
  overflow-y: auto;
}

.voucher-ticket-preview {
  max-width: 100%;
  margin: 0 auto;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  text-align: center;
  background: #f4f4f4;
  border: 1px dashed #555;
  font-family: ui-monospace, 'Cascadia Code', 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
  font-size: 0.7rem;
  line-height: 1.25;
  color: #111;
}
.voucher-ticket-preview-inner {
  display: inline-block;
  min-width: 34ch;
  padding: 1rem 0.75rem;
  box-sizing: border-box;
  vertical-align: top;
  text-align: center;
}
.preview-line.preview-separator {
  white-space: nowrap;
}
.preview-line.preview-double {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0.15rem 0;
}
.preview-line.preview-large {
  font-size: 0.85rem;
  font-weight: 700;
  margin: 0.2rem 0;
}
.preview-line.preview-bold.preview-normal {
  font-weight: 700;
}
.preview-empty-spacer {
  min-height: 0.15rem;
}
.voucher-match-list {
  max-height: 220px;
  overflow-y: auto;
}
</style> 